import * as React from "react";

/**
 * "Show this row in optimistic state until the canonical list catches
 * up" pattern. Use when:
 *
 * 1. The user fires an action whose effect is server-side and async
 *    (e.g. queueing a worker job).
 * 2. You want to show the affected rows in a "pending" state immediately.
 * 3. The canonical list (`source`) eventually drops or transforms those
 *    rows when the work completes — at which point the optimistic flag
 *    should clear automatically.
 *
 * Pure helpers below are exported so the prune/add semantics can be unit
 * tested without spinning up React.
 *
 * @example
 * ```tsx
 * const { ids: queuedIds, add: markSubmitted } = useOptimisticIds(
 *   `pendingReports:user-${userId}`,
 *   accessions,
 * );
 *
 * <Generate onSuccess={(id) => markSubmitted([id])} />
 * ```
 */

/**
 * Returns a Set with any id NOT in `visible` removed. Returns the same
 * `prev` reference unchanged if no ids needed pruning — lets the caller
 * skip a `setState` and avoid a render.
 */
export function pruneIds(
  prev: ReadonlySet<number>,
  visible: ReadonlySet<number>,
): Set<number> {
  let changed = false;
  const next = new Set(prev);
  for (const id of next) {
    if (!visible.has(id)) {
      next.delete(id);
      changed = true;
    }
  }
  return changed ? next : (prev as Set<number>);
}

/**
 * Returns a Set with `toAdd` merged in. Returns the same `prev`
 * reference if every id was already present.
 */
export function addIds(
  prev: ReadonlySet<number>,
  toAdd: Iterable<number>,
): Set<number> {
  const next = new Set(prev);
  let changed = false;
  for (const id of toAdd) {
    if (!next.has(id)) {
      next.add(id);
      changed = true;
    }
  }
  return changed ? next : (prev as Set<number>);
}

/**
 * Pluggable storage interface — matches the surface of `Storage` we
 * actually use, which makes tests trivial (a plain `Map` adapter is
 * enough) and lets us no-op gracefully when storage isn't available.
 */
export type IdStorage = {
  read(key: string): readonly number[];
  write(key: string, ids: readonly number[]): void;
};

/**
 * Browser sessionStorage adapter. Failures (quota / privacy mode /
 * SSR) are swallowed — the worst case is the optimistic state doesn't
 * survive a refresh, which is strictly cosmetic.
 */
export const sessionStorageIds: IdStorage = {
  read(key) {
    try {
      if (typeof sessionStorage === "undefined") return [];
      const raw = sessionStorage.getItem(key);
      if (!raw) return [];
      const parsed = JSON.parse(raw) as unknown;
      if (!Array.isArray(parsed)) return [];
      return parsed.filter((x): x is number => Number.isInteger(x));
    } catch {
      return [];
    }
  },
  write(key, ids) {
    try {
      if (typeof sessionStorage === "undefined") return;
      sessionStorage.setItem(key, JSON.stringify(Array.from(ids)));
    } catch {
      // sessionStorage quota / privacy mode — silently swallow.
    }
  },
};

export type UseOptimisticIdsOptions = {
  /** Override storage — primarily a test seam. Defaults to sessionStorage. */
  storage?: IdStorage;
};

export type UseOptimisticIdsResult = {
  /** Stable array snapshot of optimistic ids (changes identity on update). */
  ids: readonly number[];
  /** Mark ids as optimistic. No-op if `storageKey` is null. */
  add: (ids: readonly number[]) => void;
};

/**
 * Hook variant of {@link pruneIds} + {@link addIds} that persists the
 * set to (by default) sessionStorage so a page refresh doesn't lose
 * the "pending" UX.
 *
 * `storageKey === null` disables persistence (e.g. before the user has
 * loaded). `source === undefined` disables pruning (initial fetch).
 */
export function useOptimisticIds(
  storageKey: string | null,
  source: ReadonlyArray<{ id: number }> | undefined,
  options: UseOptimisticIdsOptions = {},
): UseOptimisticIdsResult {
  const storage = options.storage ?? sessionStorageIds;

  // Re-read storage when the key changes (e.g. user switched accounts
  // mid-session). The lazy initializer covers the first render; the
  // effect below covers subsequent key changes.
  const [ids, setIds] = React.useState<ReadonlySet<number>>(() =>
    storageKey ? new Set(storage.read(storageKey)) : new Set(),
  );

  // Track the last key we hydrated from so the effect knows when to
  // re-read vs. just persist.
  const lastKeyRef = React.useRef<string | null>(storageKey);
  React.useEffect(() => {
    if (storageKey === lastKeyRef.current) return;
    lastKeyRef.current = storageKey;
    setIds(storageKey ? new Set(storage.read(storageKey)) : new Set());
  }, [storageKey, storage]);

  // Self-prune: when `source` arrives or changes, drop any optimistic
  // ids it no longer contains. Skip when `source` hasn't loaded so we
  // don't blow away markers during the initial fetch.
  React.useEffect(() => {
    if (!source) return;
    const visible = new Set(source.map((s) => s.id));
    setIds((prev) => {
      const next = pruneIds(prev, visible);
      if (next === prev) return prev;
      if (storageKey) storage.write(storageKey, Array.from(next));
      return next;
    });
  }, [source, storageKey, storage]);

  const add = React.useCallback(
    (toAdd: readonly number[]) => {
      if (!storageKey || toAdd.length === 0) return;
      setIds((prev) => {
        const next = addIds(prev, toAdd);
        if (next === prev) return prev;
        storage.write(storageKey, Array.from(next));
        return next;
      });
    },
    [storageKey, storage],
  );

  // Memoize the array view so callers get reference stability across
  // unrelated re-renders of the host component.
  const idsArray = React.useMemo(() => Array.from(ids), [ids]);

  return { ids: idsArray, add };
}
